/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import _ from 'lodash';
import { bindActionCreators, compose } from 'redux';
import { withHOC } from './connectRequestsHOC.js'
import { connect as connectRedux } from 'react-redux';

import { initializeRequest, handleRequest, handleReset } from './actions';
import { initialState } from './reducer.js';

/**
 * Request HOC
 * Enhance Component with `request` props. Requests can be defined and then redux is use to track the request/response cycle.
 * Requests can be immeediately executed by setting onLoad=true. If not immediately executed, request is initialized so it visible
 * to other components.
 *
 * All requests defined are available globally. This allos the request HOC to behave as a cache for API calls.
 *
 * @param  {Function} mapStateToRequests Selector that defines requests. Selector receives `state` and `ownProps` params and shbould
 *                                       return a map of requests by key
 *                                       Requests may define `url`, `method`, `query`, `headers`, and `body` params
 *                                       Also 'responseAlias' and 'onLoad' to effect behavior.
 * @return {Function}                    Enhancing HOC function to wrap component
 */
export function connectRequests(mapStateToRequests) {
  function mapStateToProps(state, ownProps) {
    const requestsState = state.requests;
    const requestsConfig = mapStateToRequests(state, ownProps) || {};

    // Shallow merge requestState and requestConfig
    // Gets keys of both maps, merges keys, and creates a new object using
    // initialState, requestState, and requestConfig
    // To local components
    return _(Object.keys(requestsState))
    .concat(Object.keys(requestsConfig))
    .uniq()
    .map((requestKey) => {
      const requestState = requestsState[requestKey] || {};
      const requestConfig = requestsConfig[requestKey];
      const request = _.assign({ key: requestKey }, initialState, requestState, requestConfig);
      if (requestConfig) request.local = true;
      return request;
    })
    .keyBy('key')
    .value();
  }


  // Binds dispatch to request action creators
  function mapDispatchtToProps(dispatch) {
    const actions = {
      handleInitialization: bindActionCreators(initializeRequest, dispatch),
      handleRequest: bindActionCreators(handleRequest, dispatch),
      handleReset: bindActionCreators(handleReset, dispatch),
    };
    return actions;
  }

  // Binds action creators to individual requests
  // If request is local (defined by config) add initialization action creator and try to alias request response body
  function mergeProps(stateProps, dispatchProps, ownProps) {
    const requests = stateProps;
    const requestAliases = {};
    _.map(requests, (request, key) => {
      request.handleRequest = (requestArguments) => dispatchProps.handleRequest(key, { ...request, ...requestArguments });
      request.handleReset = () => dispatchProps.handleReset(key);

      if (request.local) {
        request.handleInitialization = () => dispatchProps.handleInitialization(request);

        if (request.responseAlias) {
          requestAliases[request.responseAlias] = request.value;
        }
      }
    });
    return { requests, requestAliases, ...ownProps };
  }


  return function Requests(WrappedComponent) {
    return compose(
      connectRedux(mapStateToProps, mapDispatchtToProps, mergeProps),
      withHOC,
    )(WrappedComponent);
  };
}

// Simple HOC function that provides request props but does not define any requests
export function withRequests(WrappedComponent) {
  return connectRequests(() => {})(WrappedComponent);
}
